import React, { Suspense } from 'react'
import { Route, Switch, Redirect } from 'react-router-dom'
import qs from 'query-string'
import {
  converge, identity, path, pipe, split, head
} from 'ramda'
import { useQuery } from 'react-apollo'

import MainLayoutRoute from './layouts/main/route'
import MyAccountRoute from './layouts/my-account/route'
import PlayerLayoutComponent from './layouts/player'
import CancelSubscriptionRoute from './layouts/cancel-subscription/route'
import SignUpLayoutRoute from './layouts/sign-up/route'

import Screen from './components/screen'
import MyAccountDetails from './components/my-account/account-details'
import SubscriptionAndPayment from './components/my-account/subscription-and-payment'
import DevicesAndDownloads from './components/my-account/devices-and-downloads'
import MyActivity from './components/my-account/my-activity'
import NotFound from './components/not-found'
import AppDownload from './components/app-download'
import ManageSubscription from './components/cancel-subscription/manage-subscription/index'
import Offer from './components/cancel-subscription/offer'
import CancelConfirmation from './components/cancel-subscription/confirmation'
import CreateAccount from './modules/sign-up/components/create-account'
import PlanIntro from './modules/sign-up/components/plan-intro'
import PlanSelect from './modules/sign-up/components/plan-select'
import AccountDetails from './modules/sign-up/components/account-details'
import Payment from './modules/sign-up/components/payment'
import PaymentConfirmation from './modules/sign-up/components/confirmation'
import Maintenance from './components/maintenance-mode'
import TVEasyLogin from './components/tv-easy-login'
import PreviewImportList from './modules/my-lists/preview-import-list'
import NotificationHub from './modules/in-app-messages/notification-hub'
import {
  UUID_SLUG,
  ACCOUNT_SCREENS,
  CANCEL_SUBSCRIPTION_SCREENS,
  LOGIN_SCREENS,
  SIGN_UP_SCREENS,
  STATIC_SCREENS
} from './constants'
import { MODALS_DO_NOT_CAUSE_SCREEN_RELOAD } from './layouts/routed-modal/routed-modal-registry'
import NAVIGATION_QUERY from '../graphql/queries/navigation.gql'
// import { segmentPage } from './segment/segment-page'

const DirectCarrierBillingModule = React.lazy(() => import('./modules/direct-carrier-billing'))
const OnboardingModule = React.lazy(() => import('./modules/content-onboarding'))

const fromGenreSlug = pipe(
  path(['match', 'params', 'genreSlug']),
  slug => `/genre/${slug}`
)

const fromUUIDSlug = pipe(
  path(['match', 'params', 'screenParams']),
  split('/'),
  head
)

const fromMovieSlug = pipe(
  path(['match', 'params', 'movieSlug']),
  slug => `/movie/${slug}`
)

const fromSeriesSlug = pipe(
  path(['match', 'params', 'seriesSlug']),
  slug => `/series/${slug}`
)

const fromPathSlug = pipe(path(['location', 'pathname']), slug => slug)

const renderScreen = getScreenId => converge((props, screenId) => <Screen screenId={screenId} {...props} />, [
  identity,
  getScreenId
])

const PlayerComponent = props => (
  <PlayerLayoutComponent featureNames={['new_player']} {...props} />
)

/**
 * Return true when navigation only opens/closes/switches a modal that should
 * keep the underlying screen mounted on the same page.
 */
const isNoReloadModalRouteChange = (currentLocation, nextLocation) => {
  if (!currentLocation || !nextLocation) return false
  if (currentLocation.pathname !== nextLocation.pathname) return false

  const currentSearch = qs.parse(currentLocation.search)
  const nextSearch = qs.parse(nextLocation.search)

  return [
    currentSearch.modal,
    nextSearch.modal
  ].some(modal => MODALS_DO_NOT_CAUSE_SCREEN_RELOAD.includes(modal))
}

const isSameRouteLocation = (currentLocation, nextLocation) => {
  if (!currentLocation || !nextLocation) return false

  return currentLocation.pathname === nextLocation.pathname &&
    currentLocation.search === nextLocation.search &&
    currentLocation.hash === nextLocation.hash
}

const Routes = props => {
  /**
   * Returns a Switch/Route react component with all the routes
   * using the Main layout (/src/app/layouts/main)
   */

  // We request both the nav header and footer before rendering the app routes.
  useQuery(NAVIGATION_QUERY)

  const getMainLayoutRoutes = () => {
    // LBX-9164: due to overage we turn off page event temp
    // useEffect(() => {
    //   segmentPage()
    // }, [window.location.pathname])

    const DefaultLayoutRoutes = (
      <Switch>
        <Route
          exact
          path="/my-lists"
          render={() => {
            return <Screen screenId="/my-lists" {...props} />
          }}
        />
        <Route
          exact
          path="/my-rentals"
          render={() => {
            return <Screen screenId="/my-rentals" {...props} />
          }}
        />
        <Route
          exact
          path="/share/watchlist/:token"
          component={PreviewImportList}
        />
        <Route
          exact
          path="/notifications"
          component={NotificationHub}
        />

        <Route
          exact
          path="/movie/:movieSlug/:rent?"
          render={renderScreen(fromMovieSlug)}
        />
        <Route
          path="/series/:seriesSlug"
          render={renderScreen(fromSeriesSlug)}
        />
        <Route path="/genre/:genreSlug" render={renderScreen(fromGenreSlug)} />
        <Route
          path={`/${UUID_SLUG}/:screenParams+`}
          component={renderScreen(fromUUIDSlug)}
        />

        {/* Route for TV easy login */}
        <Route exact path={LOGIN_SCREENS.TV_EASY_LOGIN.path} component={TVEasyLogin} />

        <Route exact path="/" component={Screen} />
        <Route exact path="/app-download" render={() => <AppDownload />} />
        <Route component={renderScreen(fromPathSlug)} />
      </Switch>
    )

    return <MainLayoutRoute path="/" component={DefaultLayoutRoutes} />
  }

  return (
    <>
      <Maintenance>
        <Switch>
          {/* Not found 404 */}
          <Route exact path="/404" component={NotFound} />

          {/* Routes that use player layout */}
          <Route exact path="/trailer/:contentId" component={PlayerComponent} />
          <Route
            path="/trailer/:videoExtraId/:contentId"
            component={PlayerComponent}
          />
          <Route exact path="/watch/:contentId" component={PlayerComponent} />

          {/* Routes that use my account layout */}
          <Redirect exact from={ACCOUNT_SCREENS.MY_ACCOUNT.path} to={ACCOUNT_SCREENS.ACCOUNT_DETAILS.path} />
          <MyAccountRoute path={ACCOUNT_SCREENS.ACCOUNT_DETAILS.path} component={MyAccountDetails} />
          <MyAccountRoute path={ACCOUNT_SCREENS.SUBSCRIPTION_PAYMENT.path} component={SubscriptionAndPayment} />
          <MyAccountRoute path={ACCOUNT_SCREENS.DEVICES_DOWNLOADS.path} component={DevicesAndDownloads} />
          <MyAccountRoute path={ACCOUNT_SCREENS.MY_ACTIVITY.path} component={MyActivity} />

          {/* Cancel Subscription Routes */}
          <Redirect exact from={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION.path} to={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_LANDING.path} />
          <CancelSubscriptionRoute path={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_LANDING.path} component={ManageSubscription} />
          <CancelSubscriptionRoute path={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_OFFER.path} component={Offer} />
          <CancelSubscriptionRoute path={CANCEL_SUBSCRIPTION_SCREENS.CANCEL_SUBSCRIPTION_CONFIRMATION.path} component={CancelConfirmation} />

          {/* Sign Up Routes */}
          <Redirect exact from="/signup" to={SIGN_UP_SCREENS.CREATE_ACCOUNT.path} />
          <Redirect exact from={SIGN_UP_SCREENS.SIGN_UP.path} to={SIGN_UP_SCREENS.CREATE_ACCOUNT.path} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.CREATE_ACCOUNT.path} component={CreateAccount} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.PLAN_INTRO.path} component={PlanIntro} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.PLAN_SELECT.path} component={PlanSelect} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.ACCOUNT_DETAILS.path} component={AccountDetails} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.PAYMENT.path} component={Payment} />
          <SignUpLayoutRoute path={SIGN_UP_SCREENS.CONFIRMATION.path} component={PaymentConfirmation} />

          {/* My Lists Routes */}
          <Redirect exact from="/my-list" to={STATIC_SCREENS.MY_LISTS.screenId} />

          {/* Other modules */}
          <Route path="/link-account">
            <Suspense fallback="">
              <DirectCarrierBillingModule />
            </Suspense>
          </Route>

          <Route path="/content-onboarding">
            <Suspense fallback="">
              <OnboardingModule />
            </Suspense>
          </Route>

          {/* Routes that use main layout */}
          {getMainLayoutRoutes()}
        </Switch>
      </Maintenance>
    </>
  )
}

export default React.memo(Routes, (currentProps, nextProps) => {
  // Avoid rebuilding the main screen routes when the URL still points to the
  // same page, or when only an allowlisted modal query param changed.
  // The modal layer renders outside Routes, so it can still update separately.
  return isSameRouteLocation(currentProps.location, nextProps.location) ||
    isNoReloadModalRouteChange(currentProps.location, nextProps.location)
})
